<template>
  <validation-observer ref="partnerOrderRequestFormValidation">
    <b-row>
      <b-col
        cols="12"
        md="7"
      >
        <b-card class="mb-1">
          <template #header>
            <div class="d-flex align-items-center justify-content-between">
              <h3 class="card-title">
                {{ $t('partner.order-request.tabs.basic-info') }}
              </h3>
              <b-button
                variant="flat-primary"
                size="sm"
                :disabled="loading"
                class="ml-3"
                @click="$bvModal.show(instructionModalId)"
              >
                <feather-icon
                  icon="HelpCircleIcon"
                  size="14"
                  class="mr-50"
                />
                {{ $t('partner.order-request.help.open') }}
              </b-button>
            </div>
          </template>

          <b-row>
            <b-col
              cols="12"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('partner.order-request.fields.requested-type')"
                rules="required"
              >
                <b-form-group>
                  <b-form-radio-group
                    :checked="form.requestedType"
                    :disabled="readonly || loading"
                    @input="updateRequestedType"
                  >
                    <b-form-radio
                      v-for="option in requestedTypeOptions"
                      :key="`requested-type-${option.value}`"
                      :value="option.value"
                      class="mt-50"
                    >
                      {{ option.label }}
                    </b-form-radio>
                  </b-form-radio-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col
              cols="12"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('partner.order-request.fields.customer')"
                rules="required"
              >
                <b-form-group :label="$t('partner.order-request.fields.customer')">
                  <v-select
                    :value="form.customerId"
                    :options="customerOptions"
                    :disabled="readonly || loading"
                    :clearable="false"
                    :searchable="false"
                    :reduce="option => option.value"
                    @input="value => updateField('customerId', value)"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col
              cols="12"
              md="6"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('partner.order-request.fields.requested-start-at')"
                rules="required"
              >
                <b-form-group :label="$t('partner.order-request.fields.requested-start-at')">
                  <flat-pickr
                    v-model="requestedStartAtModel"
                    class="form-control"
                    :config="dateTimePickerConfig"
                    :disabled="readonly || loading"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col
              v-if="isEventType"
              cols="12"
              md="6"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('order.label.base-order-info.event-end-at')"
                :rules="isEventType ? 'required' : ''"
              >
                <b-form-group :label="$t('order.label.base-order-info.event-end-at')">
                  <flat-pickr
                    v-model="requestedEndAtModel"
                    class="form-control"
                    :config="dateTimePickerConfig"
                    :disabled="readonly || loading"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col
              cols="12"
              md="6"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('partner.order-request.fields.priority')"
                rules="required"
              >
                <b-form-group :label="$t('partner.order-request.fields.priority')">
                  <v-select
                    :value="form.priority"
                    :options="priorityOptions"
                    :disabled="readonly || loading"
                    :clearable="false"
                    :searchable="false"
                    :reduce="option => option.value"
                    @input="value => updateField('priority', value)"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col
              v-if="isEventType"
              cols="12"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('order.event-kind.title')"
                :rules="isEventType ? 'required' : ''"
              >
                <b-form-group :label="$t('order.event-kind.title')">
                  <v-select
                    :value="form.eventKind"
                    :options="eventKindOptions"
                    :disabled="readonly || loading"
                    :clearable="false"
                    :searchable="false"
                    :reduce="option => option.value"
                    @input="value => updateField('eventKind', value)"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col
              v-if="showTransportFlags"
              cols="12"
              class="pb-50"
            >
              <b-form-checkbox
                :checked="form.returnTransport"
                :disabled="readonly || loading"
                @input="value => updateField('returnTransport', value)"
              >
                {{ $t('order.label.base-order-info.return-transport') }}
              </b-form-checkbox>
            </b-col>

            <b-col
              v-if="showTransportFlags"
              cols="12"
              class="pb-50"
            >
              <b-form-checkbox
                :checked="form.infectious"
                :disabled="readonly || loading"
                @input="value => updateField('infectious', value)"
              >
                {{ $t('order.label.base-order-info.infectious') }}
              </b-form-checkbox>
            </b-col>
          </b-row>
        </b-card>

        <b-card
          v-if="showPatientSection"
          class="mb-1"
          body-class="p-1"
          :title="$t('order.patient-info.title')"
        >
          <order-patient-form
            :patient="form.patient"
            :patient-index="0"
            :full-validation="true"
            :statuses-when-dispatcher-can-edit="[]"
            :is-disabled-fn="isDisabled"
            :commit-handler="emitPatientCommit"
            :convert-from-annual="convertFromAnnual"
          />
        </b-card>

        <b-card :title="$t('partner.order-request.fields.notes')">
          <b-form-group class="mb-0">
            <b-form-textarea
              :value="form.notes"
              rows="4"
              max-rows="6"
              :disabled="readonly || loading"
              @input="value => updateField('notes', value)"
            />
          </b-form-group>
        </b-card>
      </b-col>

      <b-col
        cols="12"
        md="5"
      >
        <b-card
          v-if="showPickupSection"
          class="mb-1"
          :title="$t('partner.order-request.fields.pickup')"
        >
          <validation-provider
            #default="{ errors }"
            :name="$t('partner.order-request.fields.pickup')"
            :rules="showPickupSection ? 'required' : ''"
          >
            <b-form-group
              class="mb-0"
            >
              <partner-address-form
                :value="form.pickupAddress"
                field="pickupAddress"
                :disabled="readonly || loading"
                :label="'partner.order-request.fields.pickup'"
                @change="value => updateField('pickupAddress', value)"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-card>

        <b-card :title="$t(deliverySectionTitleKey)">
          <validation-provider
            #default="{ errors }"
            :name="$t(deliverySectionTitleKey)"
            rules="required"
          >
            <b-form-group
              class="mb-0"
            >
              <partner-address-form
                :value="form.deliveryAddress"
                field="deliveryAddress"
                :disabled="readonly || loading"
                :label="deliverySectionTitleKey"
                @change="value => updateField('deliveryAddress', value)"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-card>
      </b-col>
    </b-row>

    <b-modal
      :id="instructionModalId"
      :title="$t('partner.order-request.help.title')"
      hide-footer
      centered
    >
      <template v-if="hasOrderRequestInstruction">
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="orderRequestInstruction" />
      </template>
      <b-alert
        v-else
        show
        variant="info"
        class="p-1 mb-0"
      >
        {{ $t('partner.order-request.help.empty') }}
      </b-alert>
    </b-modal>
  </validation-observer>
</template>

<script>
import moment from 'moment'
import flatPickr from 'vue-flatpickr-component'
import { Polish as PolishLocale } from 'flatpickr/dist/l10n/pl'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import {
  BAlert,
  BCard,
  BButton,
  BCol,
  BFormCheckbox,
  BFormGroup,
  BFormRadio,
  BFormRadioGroup,
  BFormTextarea,
  BModal,
  BRow,
} from 'bootstrap-vue'
import PartnerAddressForm from '@/views/external-order-request/partner/components/PartnerAddressForm.vue'
import OrderPatientForm from '@/views/orders/partials/OrderPatientForm.vue'
import { PRIORITY_CODES, getPriorityLabelKey } from '@/views/external-order-request/helpers/priority'
import orderTypes from '@/dictionaries/orderTypes'
import orderEventKinds from '@/dictionaries/orderEventKinds'

export default {
  name: 'PartnerOrderRequestForm',
  components: {
    PartnerAddressForm,
    BAlert,
    BCard,
    BButton,
    BCol,
    BFormCheckbox,
    BFormGroup,
    BFormRadio,
    BFormRadioGroup,
    BFormTextarea,
    BModal,
    BRow,
    flatPickr,
    OrderPatientForm,
    ValidationObserver,
    ValidationProvider,
    vSelect,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    allowedCustomers: {
      type: Array,
      default: () => [],
    },
    allowedOrderTypes: {
      type: Array,
      default: () => [],
    },
    orderRequestInstruction: {
      type: String,
      default: null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    form() {
      return this.value
    },
    requestedStartAtModel: {
      get() {
        return this.form.requestedStartAt
      },
      set(value) {
        this.updateField('requestedStartAt', value)
      },
    },
    requestedEndAtModel: {
      get() {
        return this.form.requestedEndAt
      },
      set(value) {
        this.updateField('requestedEndAt', value)
      },
    },
    customerOptions() {
      return this.allowedCustomers
        .filter(item => Number.isInteger(item?.id) && item.id > 0)
        .map(item => ({
          value: item.id,
          label: item.city ? `${item.name} (${item.city})` : item.name,
        }))
    },
    requestedTypeOptions() {
      const options = this.allowedOrderTypes
        .map(this.normalizeRequestedTypeOption)
        .filter(option => option.value)
      const optionValues = options.map(option => option.value)

      if (this.form.requestedType && !optionValues.includes(this.form.requestedType)) {
        options.push({
          value: this.form.requestedType,
          label: this.requestedTypeLabel(this.form.requestedType),
        })
      }

      return options
    },
    eventKindOptions() {
      return Object.values(orderEventKinds).map(kind => ({
        value: kind,
        label: this.$t(`order.event-kind.${kind}`),
      }))
    },
    priorityOptions() {
      return PRIORITY_CODES.map(code => ({
        value: code,
        label: this.$t(getPriorityLabelKey(code)),
      }))
    },
    hasOrderRequestInstruction() {
      return typeof this.orderRequestInstruction === 'string'
        && this.orderRequestInstruction.trim() !== ''
    },
    instructionModalId() {
      return 'partner-order-request-instruction-modal'
    },
    dateTimePickerConfig() {
      return {
        locale: PolishLocale,
        enableTime: true,
        dateFormat: 'Y-m-d H:i',
        disableMobile: true,
      }
    },
    isEventType() {
      return this.form.requestedType === orderTypes.event
    },
    isMedicalCourierType() {
      return this.form.requestedType === orderTypes.medical_courier
    },
    showTransportFlags() {
      return !this.isMedicalCourierType && !this.isEventType
    },
    showPatientSection() {
      return !this.isMedicalCourierType && !this.isEventType
    },
    showPickupSection() {
      return !this.isEventType
    },
    deliverySectionTitleKey() {
      return this.isEventType
        ? 'partner.order-request.fields.execution-location'
        : 'partner.order-request.fields.delivery'
    },
  },
  methods: {
    isDisabled() {
      return this.readonly || this.loading
    },
    updateField(field, value) {
      this.$emit('input', {
        ...this.form,
        [field]: value,
      })
    },
    updateRequestedType(value) {
      const nextForm = {
        ...this.form,
        requestedType: value,
      }

      if (value === orderTypes.event) {
        if (!nextForm.eventKind) {
          nextForm.eventKind = this.eventKindOptions[0]?.value || null
        }
      } else {
        nextForm.eventKind = null
        nextForm.requestedEndAt = ''
      }

      if (value === orderTypes.event || value === orderTypes.medical_courier) {
        nextForm.returnTransport = false
        nextForm.infectious = false
      }

      this.$emit('input', nextForm)
    },
    emitPatientCommit() {
      this.updateField('patient', {
        ...this.form.patient,
      })
    },
    convertFromAnnual(patient) {
      if (!patient || patient.age === null || patient.age === undefined || patient.age === '') {
        return
      }

      const ageString = patient.age.toString()
      const yearShort = parseInt(moment().format('YY'), 10)
      const yearFull = parseInt(moment().format('YYYY'), 10)
      let nextAge

      if (ageString.length < 3) {
        nextAge = yearShort < patient.age
          ? yearShort - patient.age + 100
          : yearShort - patient.age
      } else {
        nextAge = yearFull - patient.age
      }

      this.$set(patient, 'age', nextAge)
      this.emitPatientCommit()
    },
    requestedTypeLabel(type) {
      if (!type) {
        return '-'
      }

      const i18nKey = `order.types.${type}`
      if (typeof this.$te === 'function' && this.$te(i18nKey)) {
        return this.$t(i18nKey)
      }

      return type
    },
    normalizeRequestedTypeOption(type) {
      if (typeof type === 'string') {
        return {
          value: type,
          label: this.requestedTypeLabel(type),
        }
      }

      if (type && typeof type === 'object') {
        const value = typeof type.value === 'string' ? type.value : ''
        const fallbackLabel = typeof type.text === 'string' ? type.text : value

        return {
          value,
          label: this.requestedTypeLabel(value) || fallbackLabel,
        }
      }

      return {
        value: '',
        label: '',
      }
    },
  },
}
</script>

<style lang="scss">
@import '~flatpickr/dist/flatpickr.min.css';
</style>
